import React from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import HomeMainPage from "./HomeMainPage";
import { GlobalProvider } from "./context/GlobalContext";
import { TranslateProvider } from "./context/TranslateContext";
import GetTrans from "./utils/GetTrans";
import { FlowProvider } from "./context/FlowContext";

const typography = {
  fontFamily: "Outfit",
  h1: {
    fontWeight: 700,
    fontSize: "2.25rem",
    lineHeight: "2.75rem",
    fontFamily: "Outfit",
  },
  h2: {
    fontWeight: 700,
    fontSize: "1.875rem",
    lineHeight: "2.25rem",
    fontFamily: "Outfit",
  },
  h3: {
    fontWeight: 700,
    fontSize: "1.5rem",
    lineHeight: "1.75rem",
    fontFamily: "Outfit",
  },
  h4: {
    fontWeight: 700,
    fontSize: "1.3125rem",
    lineHeight: "1.6rem",
  },
  h5: {
    fontWeight: 700,
    fontSize: "1.125rem",
    lineHeight: "1.6rem",
  },
  h6: {
    fontWeight: 700,
    fontSize: "1rem",
    lineHeight: "1.2rem",
  },
  button: {
    textTransform: "capitalize",
    fontWeight: 600,
  },
  body1: {
    fontSize: "0.95rem",
    fontWeight: 500,
    lineHeight: "1.5rem",
  },
  body2: {
    fontSize: "0.85rem",
    letterSpacing: "0rem",
    fontWeight: 500,
    lineHeight: "1.25rem",
  },
  subtitle1: {
    fontSize: "0.95rem",
    fontWeight: 600,
  },
  subtitle2: {
    fontSize: "0.85rem",
    fontWeight: 600,
  },
  caption: {
    fontSize: "0.8rem",
    fontWeight: 500,
    lineHeight: "1.2rem",
  },
};

const shadows = [
  "none",
  "0px 2px 8px rgba(0,0,0,0.15)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 2px 4px -1px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 4px 8px -2px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 6px 12px -2px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 8px 16px -2px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 10px 20px -2px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 12px 24px -2px rgba(0,0,0,0.3)",
  "0 12px 24px rgb(0,0,0,0.12)",
  "rgb(145 158 171 / 40%) 0px 0px 3px 0px, rgb(145 158 171 / 20%) 0px 12px 24px -4px",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 14px 28px -4px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 16px 32px -4px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 18px 36px -4px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 20px 40px -8px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 22px 44px -8px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 24px 48px -8px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 26px 52px -8px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 28px 56px -8px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 30px 60px -8px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 32px 64px -8px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 34px 68px -8px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 36px 72px -8px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 38px 76px -8px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 40px 80px -8px rgba(0,0,0,0.3)",
  "0 0 2px 0 rgba(0,0,0,0.4), 0 42px 84px -8px rgba(0,0,0,0.3)",
];

const ColorModeContext = React.createContext({ toggleColorMode: () => {} });

export default function ToggleColorMode() {
  const { trans } = GetTrans();

  const [mode, setMode] = React.useState("dark");
  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
    }),
    [],
  );

  React.useEffect(() => {
    setMode(
      localStorage.getItem("theme")
        ? localStorage.getItem("theme")
        : (localStorage.setItem("theme", "dark"), "dark"),
    );
  }, []);

  const theme = React.useMemo(
    () =>
      createTheme({
        components: {
          MuiButton: {
            defaultProps: {
              disableElevation: true,
            },
            styleOverrides: {
              root: {
                borderRadius: 10,
                textTransform: "capitalize",
                fontWeight: 600,
                fontSize: "0.95rem",
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
        },
        typography: typography,
        shadows,
        palette: {
          mode,
          primary: {
            main: trans ? trans.color_theme : "#22a454",
            light: trans ? trans.color_theme + "33" : "#22a45433",
            dark: trans ? trans.color_theme : "#1a8043",
          },
          secondary: {
            main: "#9c27b0",
            light: "#ba68c8",
            dark: "#7b1fa2",
          },
          ...(mode === "light"
            ? {
                // Light Mode - High Contrast
                background: {
                  default: "#f8f9fa",
                  paper: "#ffffff",
                },
                text: {
                  primary: "#1a1a1a",
                  secondary: "#4a4a4a",
                  disabled: "#9e9e9e",
                },
                divider: "rgba(0, 0, 0, 0.12)",
                action: {
                  active: "rgba(0, 0, 0, 0.7)",
                  hover: "rgba(0, 0, 0, 0.08)",
                  selected: "rgba(0, 0, 0, 0.14)",
                  disabled: "rgba(0, 0, 0, 0.26)",
                  disabledBackground: "rgba(0, 0, 0, 0.12)",
                },
              }
            : {
                // Dark Mode - High Contrast
                background: {
                  default: "#0a0a0a",
                  paper: "#1a1a1a",
                },
                text: {
                  primary: "#ffffff",
                  secondary: "#e0e0e0",
                  disabled: "#757575",
                },
                divider: "rgba(255, 255, 255, 0.15)",
                action: {
                  active: "rgba(255, 255, 255, 0.9)",
                  hover: "rgba(255, 255, 255, 0.12)",
                  selected: "rgba(255, 255, 255, 0.2)",
                  disabled: "rgba(255, 255, 255, 0.3)",
                  disabledBackground: "rgba(255, 255, 255, 0.12)",
                },
              }),
        },
      }),
    [mode, trans],
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <GlobalProvider>
        <TranslateProvider>
          <FlowProvider>
            <ThemeProvider theme={theme}>
              <HomeMainPage />
            </ThemeProvider>
          </FlowProvider>
        </TranslateProvider>
      </GlobalProvider>
    </ColorModeContext.Provider>
  );
}
